<template>
  <div>
    <div class="card shadow-sm border-0">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <button class="btn btn-link mb-0 p-0" type="button" :disabled="exportando" @click="onExportar">
            <i class="bi bi-file-earmark-arrow-down" /> {{ $t("exportar") }}
          </button>

          <span class="badge" :class="statusClass">
            {{ statusTexto }}
          </span>
        </div>

        <LogsFilters @filter="onFilter" @clear="onClearFilters" />

        <div v-if="!conectado" class="alert alert-danger mb-3">
          {{ $t("erroConexaoStreamLogs") }}
        </div>

        <div class="table-responsive logs-table">
          <table class="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th style="width: 190px;">
                  {{ $t("dataHoraCriacao") }}
                </th>
                <th style="width: 100px;">
                  {{ $t("logNivel") }}
                </th>
                <th>
                  {{ $t("mensagem") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredRows.length === 0">
                <td colspan="3" class="text-center text-muted py-4">
                  {{ $t("nenhumLogParaExibir") }}
                </td>
              </tr>
              <tr v-for="log in filteredRows" :key="log.id">
                <td class="font-monospace small">
                  {{ log.timestamp }}
                </td>
                <td>
                  <span class="badge" :class="badgeNivel(log.level)">
                    {{ log.level }}
                  </span>
                </td>
                <td class="font-monospace small text-break">
                  {{ log.message }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { conectarStreamLogs, exportarLogs } from '../logsService';
import LogsFilters from './LogsFilters.vue';

const LIMITE_LOGS = 500;
const BADGES_NIVEL = {
  error: 'text-bg-danger',
  warn: 'text-bg-warning',
  info: 'text-bg-info',
  debug: 'text-bg-secondary',
};

export default {
  components: { LogsFilters },
  data: () => ({
    table: {
      rows: [],
    },
    filters: {
      level: 'todos',
      search: '',
    },
    conectado: false,
    desconectar: null,
    tentandoReconectar: false,
    timeoutReconexao: null,
    exportando: false,
  }),
  computed: {
    filteredRows() {
      const termo = this.filters.search.trim().toLowerCase();

      return this.table.rows
        .filter((log) => this.filters.level === 'todos' || log.level === this.filters.level)
        .filter((log) => (termo ? (log.message || '').toLowerCase().includes(termo) : true))
        .slice()
        .reverse();
    },
    statusTexto() {
      if (this.conectado) return this.$t('logConectado');
      if (this.tentandoReconectar) return this.$t('logReconectando');
      return this.$t('logDesconectado');
    },
    statusClass() {
      if (this.conectado) return 'text-bg-success';
      if (this.tentandoReconectar) return 'text-bg-warning';
      return 'text-bg-secondary';
    },
  },
  mounted() {
    this.iniciarStream();
  },
  beforeUnmount() {
    this.pararStream();
  },
  methods: {
    onFilter(filters) {
      this.filters = { ...filters };
    },
    onClearFilters() {
      this.filters = {
        level: 'todos',
        search: '',
      };
      this.limparLogs();
    },
    normalizarLog(log) {
      return {
        id: log.id || `${Date.now()}-${Math.random()}`,
        timestamp: log.timestamp || '-',
        level: String(log.level || 'info').toLowerCase(),
        message: String(log.message || '-'),
      };
    },
    async iniciarStream() {
      this.pararStream();
      this.tentandoReconectar = false;

      try {
        this.desconectar = await conectarStreamLogs({
          onInit: ({ logs }) => {
            this.table.rows = Array.isArray(logs) ? logs.map(this.normalizarLog).slice(-LIMITE_LOGS) : [];
            this.conectado = true;
          },
          onLog: (log) => {
            this.table.rows.push(this.normalizarLog(log));
            if (this.table.rows.length > LIMITE_LOGS) {
              this.table.rows.splice(0, this.table.rows.length - LIMITE_LOGS);
            }
          },
          onClose: this.marcarDesconectado,
          onError: this.marcarDesconectado,
        });
      } catch (error) {
        this.marcarDesconectado();
      }
    },
    async onExportar() {
      if (this.exportando) return;
      this.exportando = true;

      try {
        const { blob, nomeArquivo } = await exportarLogs();
        this.baixarArquivo(blob, nomeArquivo);
      } finally {
        this.exportando = false;
      }
    },
    baixarArquivo(blob, nomeArquivo) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = nomeArquivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    marcarDesconectado() {
      this.conectado = false;
      this.reconectar();
    },
    reconectar() {
      if (this.timeoutReconexao) return;
      this.tentandoReconectar = true;

      this.timeoutReconexao = setTimeout(() => {
        this.timeoutReconexao = null;
        this.iniciarStream();
      }, 3000);
    },
    pararStream() {
      if (this.timeoutReconexao) {
        clearTimeout(this.timeoutReconexao);
        this.timeoutReconexao = null;
      }

      if (this.desconectar) {
        this.desconectar();
        this.desconectar = null;
      }
    },
    limparLogs() {
      this.table.rows = [];
    },
    badgeNivel(level) {
      return BADGES_NIVEL[level] || 'text-bg-dark';
    },
  },
};
</script>

<style scoped>
.logs-table {
  max-height: 65vh;
}
</style>
